// Follows the signed-in user's account with streamMyChanges for as long as they are signed in, and hands each change to
// whatever on the page shows it: lists, the account page, the library, the theme. Claude's edits through MCP and another
// tab's or app's arrive the moment they commit; this tab's own writes are skipped by their X-Lureo-Client-Session.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { components } from '@/generated/contracts/api-v1';
import type { ChangeCacheSeq } from '@/infrastructure/api/v1/routing';
import { CLIENT_SESSION } from '@/infrastructure/clients/BackendClient';
import { accountChangedElsewhere, ownChangeEchoed } from '@/infrastructure/api/v1/client';
import { STREAM_BACKOFF_MS, readEventStream } from './eventStream';

type Schemas = components['schemas'];
/** A change to the account, with the version of the cache the stream's drop of it reached (routing.ts). */
export type AccountChangeEvent = Schemas['AccountChangeEvent'] & ChangeCacheSeq;
export type AccountEntity = Schemas['AccountChangeEntity'];

/** Changes to the listened entities, gathered; empty when changes may have been missed (the stream reopened after a gap) and everything is read again. */
export type AccountChangesHandler = (events: AccountChangeEvent[]) => void;

interface Listener {
  entities: ReadonlySet<AccountEntity>;
  handler: AccountChangesHandler;
  wait: number;
  pending: AccountChangeEvent[];
  missed: boolean;
  timer?: ReturnType<typeof setTimeout>;
}

const listeners = new Set<Listener>();

const flush = (listener: Listener) => {
  const events = listener.missed ? [] : listener.pending;
  listener.pending = [];
  listener.missed = false;
  listener.timer = undefined;
  try {
    listener.handler(events);
  } catch (error) {
    console.warn('[accountChanges] a listener failed', error);
  }
};

const schedule = (listener: Listener) => {
  listener.timer ??= setTimeout(() => flush(listener), listener.wait);
};

function deliver(event: AccountChangeEvent) {
  for (const listener of listeners) {
    if (!listener.entities.has(event.entity)) continue;
    listener.pending.push(event);
    schedule(listener);
  }
}

// What changed while the stream was down was never sent: every listener reads again
function missed() {
  accountChangedElsewhere();
  for (const listener of listeners) {
    listener.missed = true;
    schedule(listener);
  }
}

/**
 * Calls `handler` with the account's changes to `entities` from elsewhere, gathered over `wait` ms (a burst, such as a
 * scene save touching its project, arrives as one call). Returns the unsubscribe.
 */
export function onAccountChanges(entities: readonly AccountEntity[], handler: AccountChangesHandler, wait = 250): () => void {
  const listener: Listener = { entities: new Set(entities), handler, wait, pending: [], missed: false };
  listeners.add(listener);
  return () => {
    clearTimeout(listener.timer);
    listeners.delete(listener);
  };
}

/** onAccountChanges for a component: the latest `handler` runs, and it stops listening when the component goes. */
export function useAccountChanges(entities: readonly AccountEntity[], handler: AccountChangesHandler, wait = 250): void {
  const latest = useRef(handler);
  useLayoutEffect(() => {
    latest.current = handler;
  });
  const key = entities.join('|');
  useEffect(() => onAccountChanges(key.split('|') as AccountEntity[], events => latest.current(events), wait), [key, wait]);
}

/** A number that goes up with each batch of changes to `entities`, so an effect that depends on it reads again. */
export function useAccountChangeVersion(entities: readonly AccountEntity[], wait = 250): number {
  const [version, setVersion] = useState(0);
  useAccountChanges(entities, () => setVersion(n => n + 1), wait);
  return version;
}

/**
 * In an effect that reads again on `version`: whether it moved since the effect last ran (`seen` keeps the last), so this
 * read is a live refresh that keeps what is shown until the new rows arrive rather than going back to loading.
 */
export function isLiveRefresh(seen: { current: number }, version: number): boolean {
  const live = seen.current !== version;
  seen.current = version;
  return live;
}

/**
 * Opens the account's change stream and keeps it open until the returned function is called or the session ends: a
 * stream that ends, is turned away for a while (a 403 or 404 during a deploy, a refresh that failed) or reports access
 * ended (its bearer expired) is opened again with backoff, with a fresh bearer, and listeners read again.
 */
export function followAccountChanges(): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let wake: (() => void) | null = null;

  const run = async () => {
    let attempt = 0;
    let reopened = false;
    while (!stopped) {
      controller = new AbortController();
      const outcome = await readEventStream<AccountChangeEvent>('/api/v1/users/me/changes', controller, () => {
        attempt = 0;
        if (reopened) missed();
        reopened = true;
      }, event => {
        if (event.op === 'accessEnded') {
          controller?.abort();
          return;
        }
        // This tab's own change: no screen hears of it, but its version is taken all the same
        if (event.originSession === CLIENT_SESSION) {
          ownChangeEchoed(event.cacheSeq);
          return;
        }
        // What screens read as they opened may no longer be what the server holds; their reads again wait for its version
        accountChangedElsewhere(event.cacheSeq);
        deliver(event);
      }, 'accountChanges');
      if (stopped || outcome === 'signedOut') return;
      const wait = STREAM_BACKOFF_MS[Math.min(attempt, STREAM_BACKOFF_MS.length - 1)];
      attempt += 1;
      await new Promise<void>(resolve => {
        const timer = setTimeout(resolve, wait);
        wake = () => {
          clearTimeout(timer);
          resolve();
        };
      });
      wake = null;
    }
  };
  void run();

  // Back online (a laptop waking): no need to sit out the rest of the backoff
  const reconnect = () => wake?.();
  window.addEventListener('online', reconnect);

  return () => {
    stopped = true;
    window.removeEventListener('online', reconnect);
    controller?.abort();
    wake?.();
  };
}
